import { useEffect, useState } from "react";

/**
 * Safely retrieves and parses a value from localStorage
 * @param key - The localStorage key
 * @param defaultValue - Default value if key doesn't exist or parsing fails
 * @returns The parsed value or default value
 */
export function getLocalStorage<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item) as T;
  } catch (error) {
    console.error(`Failed to parse localStorage key "${key}":`, error);
    return defaultValue;
  }
}

/**
 * Safely stringifies and stores a value in localStorage
 * @param key - The localStorage key
 * @param value - The value to store
 */
export function setLocalStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to save to localStorage key "${key}":`, error);
  }
}

/**
 * Removes a value from localStorage
 * @param key - The localStorage key
 */
export function removeLocalStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Failed to remove localStorage key "${key}":`, error);
  }
}

/**
 * Custom hook that syncs state with localStorage
 * @param key - The localStorage key
 * @param defaultValue - Default value if key doesn't exist
 * @returns A stateful value and setter function
 *
 * @example
 * const [settings, setSettings] = useLocalStorage('userSettings', { theme: 'dark' });
 */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prevValue: T) => T)) => void] {
  // Initialize state with value from localStorage or default
  const [state, setState] = useState<T>(() => {
    return getLocalStorage(key, defaultValue);
  });

  // Save to localStorage whenever state changes
  useEffect(() => {
    setLocalStorage(key, state);
  }, [key, state]);

  return [state, setState];
}
